import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './overlay';
import { useConfirmDialog } from './overlay/ConfirmDialog';

/**
 * Reusable form modal — uses <Modal> overlay with standardized structure.
 * Props:
 *  - title: string
 *  - fields: [{key, label, type, options, required, placeholder, defaultValue}]
 *  - initialValues: object (for edit mode)
 *  - onSave: (data) => Promise<void>
 *  - onCancel: () => void
 *  - onDelete: () => Promise<void> (optional, for edit mode)
 */
export default function FormModal({ title, fields, initialValues, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialogElement } = useConfirmDialog();

  useEffect(() => {
    const init = {};
    fields.forEach(f => {
      init[f.key] = initialValues?.[f.key] ?? f.defaultValue ?? (f.type === 'toggle' ? false : '');
    });
    setValues(init);
  }, [fields, initialValues]);

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleSave = async () => {
    const newErrors = {};
    fields.forEach(f => {
      if (f.required && (values[f.key] === '' || values[f.key] === null || values[f.key] === undefined)) {
        newErrors[f.key] = t('form.required');
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      // Convert number fields
      const data = { ...values };
      fields.forEach(f => {
        if (f.type === 'number' && data[f.key] !== '' && data[f.key] !== null) {
          data[f.key] = parseFloat(data[f.key]);
        }
      });
      await onSave(data);
    } catch (e) {
      console.error('Save failed:', e);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('form.deleteConfirmTitle', { defaultValue: 'Eintrag löschen?' }),
      description: t('form.deleteConfirmBody', { defaultValue: 'Diese Aktion kann nicht rückgängig gemacht werden.' }),
      destructive: true,
    });
    if (!ok) return;
    setSaving(true);
    try { await onDelete?.(); } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <>
    <Modal.Root open onOpenChange={(open) => { if (!open) onCancel?.(); }}>
      <Modal.Content size="md" title={title}>
        <Modal.Body>
          <div className="space-y-4">
            {fields.map(f => (
              <div key={f.key}>
                <label className="text-xs font-medium text-[var(--aif-text-secondary)] mb-1 block">
                  {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                </label>

                {f.type === 'select' ? (
                  <select
                    value={values[f.key] || ''}
                    onChange={e => handleChange(f.key, e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-sm bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] focus:border-[var(--aif-color-primary)] outline-none"
                  >
                    <option value="">{f.placeholder || t('form.select')}</option>
                    {(f.options || []).map(o => (
                      <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
                        {typeof o === 'string' ? o : o.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea
                    value={values[f.key] || ''}
                    onChange={e => handleChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-xl text-sm bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] focus:border-[var(--aif-color-primary)] outline-none resize-none"
                  />
                ) : f.type === 'toggle' ? (
                  <button
                    onClick={() => handleChange(f.key, !values[f.key])}
                    className={`w-12 h-6 rounded-full transition-colors ${values[f.key] ? 'bg-[var(--aif-color-primary)]' : 'bg-[var(--aif-border-default)]'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${values[f.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                ) : (
                  <input
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                    inputMode={f.type === 'number' ? 'decimal' : undefined}
                    value={values[f.key] || ''}
                    onChange={e => handleChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    step={f.type === 'number' ? 'any' : undefined}
                    className="w-full px-3 py-2.5 rounded-xl text-sm bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] focus:border-[var(--aif-color-primary)] outline-none"
                  />
                )}

                {errors[f.key] && (
                  <p className="text-xs text-red-400 mt-1">{errors[f.key]}</p>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {onDelete && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="mr-auto px-4 py-2.5 rounded-xl text-sm text-red-400 border border-red-400/30 hover:bg-red-400/10 transition disabled:opacity-40"
            >
              {t('form.delete')}
            </button>
          )}
          <Modal.Close asChild>
            <button onClick={onCancel} className="px-4 py-2.5 rounded-xl text-sm text-[var(--aif-text-secondary)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-surface)] transition">
              {t('form.cancel')}
            </button>
          </Modal.Close>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl text-sm font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition disabled:opacity-40"
          >
            {saving ? t('form.saving') : t('form.save')}
          </button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
    {ConfirmDialogElement}
    </>
  );
}
